import React, { useState, useRef, useCallback } from 'react'
import html2canvas from 'html2canvas'
import PptxGenJS from 'pptxgenjs'

/* ━━━ データ定義 ━━━ */

// Findee を構成する3モジュール（コア1 + オプション2）
const MODULES = [
  {
    tag: 'CORE',
    icon: '🛒',
    name: 'AI購買支援（接客チャット）',
    desc: '会話で「選ぶ」を助ける。リッチUI 10種を文脈で出し分け、比較・提案・決定まで伴走',
    color: '#6366f1',
  },
  {
    tag: 'OPTION',
    icon: '🔍',
    name: '検索サジェスト',
    desc: '検索窓の入力に即応。キーワード／商品／記事を提示し、0件離脱を「もしかして」と意味検索で救う',
    color: '#f59e0b',
  },
  {
    tag: 'OPTION',
    icon: '#️⃣',
    name: 'Tag Trail',
    desc: '買い手の言葉でタグ体系を作り、商品詳細・SEOタグページ・サジェストの3つの出口へ供給',
    color: '#ec4899',
  },
]

const MARKET_STATS = [
  { num: '4.4×', label: 'AI接客導入で平均CVR向上', src: 'EC接客AIレポート 2026' },
  { num: '67%', label: '商品選びに迷うEC顧客の割合', src: 'EC購買行動調査 2026' },
  { num: '38%', label: '会話AI導入による平均AOV増加', src: 'Conversational Commerce Index' },
  { num: '92%', label: '購買前にレコメンドを参考にする消費者', src: 'NRF Retail Insights 2026' },
]

const TRENDS = [
  { icon: '💬', title: 'チャットコマースが標準に', desc: 'Z世代を中心に「商品ページを見て買う」から「チャットで相談して買う」への移行が加速。LINE・Webチャットでの購買支援が必須に', color: '#6366f1' },
  { icon: '🎨', title: 'リッチUI型対話の台頭', desc: 'テキストだけでなく、商品ギャラリー・比較表・Heroカード等のリッチコンポーネントを文脈に応じて出し分ける接客が主流化', color: '#8b5cf6' },
  { icon: '🧩', title: '役割特化AIの分業', desc: '1体の万能AIではなく「接客AI」と「問合せ対応AI」を分け、必要なときだけ引き継ぐ設計へ。責任範囲と学習データを分けたほうが両方の精度が上がる', color: '#ec4899' },
  { icon: '🚪', title: '入口から出口までAI化', desc: 'チャットだけでなく、検索窓のサジェストとタグによる回遊導線までAIが担う。サイト内の「探す・比べる・見つける」を一気通貫で最適化', color: '#f59e0b' },
]

const PAIN_POINTS = [
  { problem: '商品ページだけでは選びきれない', impact: '商品数が多いほどユーザーは決断疲れ。比較・絞り込みの会話的サポートがないと離脱率が増加', color: '#ef4444' },
  { problem: '検索窓が0件を返して離脱する', impact: '検索した人はもっとも買う気がある。にもかかわらず表記ゆれ・ひらがな入力・言い換えで0件になり、その場で離れてしまう', color: '#f59e0b' },
  { problem: 'カテゴリ階層だけでは回遊しない', impact: '「敏感肌向け」「ギフト」のような買い手の言葉で横断できず、回遊もSEOの受け皿も作れない', color: '#ec4899' },
  { problem: '接客と問合せを1体のAIに詰め込む', impact: '商品提案と注文・配送・返品対応では必要な知識も責任範囲も違う。無理に統合するとどちらの精度も落ちる', color: '#8b5cf6' },
  { problem: '在庫切れ・旧価格のまま推薦される', impact: '埋め込み時点のスナップショットだけを見ていると、売れない商品や誤った価格を案内してクレームになる', color: '#3b82f6' },
  { problem: '効果測定が見えない', impact: 'AI接客のCVR・GMV貢献度が不透明。投資対効果が示せず予算化が難しい', color: '#10b981' },
]

// Findee が担う範囲 / AIスタッフ（別サービス）が担う範囲
const SCOPE_COMPARE = [
  {
    key: 'findee',
    badge: '本サービス',
    icon: '🛒',
    title: 'Findee ─ 「選ぶ」を助ける接客',
    color: '#6366f1',
    items: [
      '商品を探す・比べる・決めるまでの伴走',
      '商品理解・在庫・価格・販促を踏まえた推薦',
      '検索サジェストとタグによる回遊の設計',
      '購買につながったかの計測と継続改善',
    ],
    example: '「乾燥肌向けの化粧水は？」\n「AとBの違いを比べたい」\n「予算1万円でギフトを選んで」',
  },
  {
    key: 'ai_staff',
    badge: '別サービス',
    icon: '🎧',
    title: 'AIスタッフ ─ 「困った」を解く問合せ対応',
    color: '#64748b',
    items: [
      '注文状況・配送・納期の問合せ',
      '返品・交換・キャンセルの手続き案内',
      '会員情報・ポイント・支払い方法',
      '有人オペレータへの引き継ぎ',
    ],
    example: '「注文した商品はいつ届く？」\n「サイズ交換したい」\n「ポイントの有効期限は？」',
  },
]

const SEAMLESS_ITEMS = [
  {
    icon: '🔀',
    title: '引き継ぎトリガーの判定',
    desc: 'Findee 側のNGワード・禁止トピック検知で「これは問合せだ」と判定した会話を引き継ぎ対象にし、引き継ぎ先（人間 / AIスタッフ）を会話に記録する',
    status: '実装済み',
    color: '#10b981',
  },
  {
    icon: '📦',
    title: '商品マスタの共有',
    desc: '連携マスタAPI（配信コード＋APIキー認証）で、AIスタッフ側が SmartHub の商品マスタ・商品URL・商品画像を参照。両サービスが同じ商品情報を話す',
    status: '実装済み',
    color: '#10b981',
  },
  {
    icon: '🧭',
    title: '訪問者の共通識別と計測',
    desc: '計測タグの `w2_vid`（Cookie）で訪問者を共通識別。Findee 経由の購入は注文番号・明細ベースで計上し、重複計上と取りこぼしを防ぐ',
    status: '実装済み',
    color: '#10b981',
  },
  {
    icon: '💬',
    title: '会話コンテキストの受け渡し',
    desc: '接客の途中で問合せに変わったとき、そこまでの会話をそのまま AIスタッフ に渡して聞き直しを無くす。現在は引き継ぎ判定までを実装済み',
    status: '開発予定',
    color: '#f59e0b',
  },
]

const WORKFLOW_STEPS = [
  { num: '01', title: '配信設定・設置面の設計', icon: '📡', desc: '対象ページ・起動条件・デザインを配信単位で設定。マルチエントリ方式で、1ページに入力欄・起動タブ・インライン枠をいくつでも併置できる', color: '#6366f1' },
  { num: '02', title: 'プロファイル・オープニング質問・ナレッジ', icon: '🎭', desc: '応答トーンをプロファイル化。ページごとに最初の設問を出し分け、よくある質問はナレッジでLLM呼び出し前に即答', color: '#8b5cf6' },
  { num: '03', title: 'プリセット作成（Hero/バンドル/二択）', icon: '📌', desc: '押し出したい商品・セット・絞り込みカードを事前登録。強制発火 or LLM補助の両モード対応', color: '#a78bfa' },
  { num: '04', title: 'リッチUI × Tool Use 動的応答', icon: '🎨', desc: 'AIが会話文脈を読み、10種コンポーネントから最適UIを自動選択。在庫・販売可否・価格・販促は応答直前にライブ参照して事実を担保', color: '#c084fc' },
  { num: '05', title: 'NG検知・引き継ぎ判定・CV計測', icon: '🛡️', desc: 'NGワード・禁止トピックを入出力で検知し、問合せはAIスタッフ／有人へ引き継ぎ。購入は注文番号・明細ベースで計上', color: '#e879f9' },
  { num: '06', title: '会話からの学習・A/Bテスト', icon: '🧪', desc: '会話をナレッジ化して応答を底上げしつつ、配信ON/OFF・A/Bを統計判定で評価。改善ループを回す', color: '#f472b6' },
]

const CURRENT_FEATURES = [
  {
    category: '設置・配信',
    icon: '📡',
    color: '#6366f1',
    features: [
      { name: 'マルチエントリ設置', desc: '1ページに入力欄・起動ボタン・インライン枠を併置。会話状態は全エントリで共有' },
      { name: '呼び出しUIの形態選択', desc: 'ラベル付き縦タブ（既定）／円形FAB。AIスタッフとの視覚的な混同を回避' },
      { name: 'サイドパネル／ボトムシート', desc: 'PCはサイドパネル、SPはボトムシート。RUFUS風のエージェント表現に対応' },
      { name: '配信設定（マルチキャンペーン）', desc: '対象ページを前方一致・完全一致・正規表現で実行時に判定して出し分け' },
      { name: 'APIキー＋ドメイン制限', desc: '配信ごとに発行。ドメイン検証＋Rate Limit でセキュアに公開' },
      { name: 'A/Bテスト3種対応', desc: 'ON/OFF・配信A vs B・単一要素A/B（配分比率を可変）' },
    ]
  },
  {
    category: 'AI対話・リッチUI',
    icon: '🎨',
    color: '#8b5cf6',
    features: [
      { name: 'リッチUI 10コンポーネント', desc: 'Gallery/Hero/比較表/Radar/個別/二択/Filter/Review/B&A/Bundle' },
      { name: 'Anthropic Tool Use 構造化応答', desc: 'Claude が会話文脈から最適UIを自動選択して構造化データで返す' },
      { name: 'プリセット強制発火', desc: 'キーワードヒットでLLM呼び出し前にプリセットを即時表示' },
      { name: 'コンポーネント別オプション', desc: 'グリッド列数・パレット・ハイライト色等を細かく設定可能' },
      { name: 'フォローアップチップ', desc: '次の質問候補を自動生成し、深掘り対話を促進' },
      { name: '入場アニメ＋緊急性カウントダウン', desc: 'fade/slide/zoom＋期限カウントダウンで訴求を強化' },
    ]
  },
  {
    category: '会話の入口設計',
    icon: '💡',
    color: '#ec4899',
    features: [
      { name: 'オープニング質問セット', desc: 'AI側から設問を投げる形にして、空白スタートの離脱を防ぐ（1セット最大5問）' },
      { name: 'ページ条件での出し分け', desc: 'URL・商品ID・カテゴリIDと優先度で、特集や商品詳細ごとに設問を固定' },
      { name: '固定モード / AI生成モード', desc: '運営者が書いた設問をそのまま出すか、ページ文脈と商品理解から自動生成するか' },
      { name: '生成結果のページ単位キャッシュ', desc: '表示のたびに生成を走らせず、レスポンスとコストを抑制' },
      { name: '回答を積み上げて1通目を合成', desc: '設問への回答をまとめて要約文にし、通常の対話経路へそのまま流す' },
      { name: '回答の構造化記録', desc: '設問キーと回答をメタに保存し「どう答えた人が買ったか」を分析可能に' },
    ]
  },
  {
    category: '推薦精度（商品理解・実データ）',
    icon: '🧠',
    color: '#f59e0b',
    features: [
      { name: '商品意味理解（8軸抽出）', desc: 'LLMで用途・特徴・素材・色柄などを抽出し、意味検索の母数を底上げ' },
      { name: 'ベクトル意味検索（pgvector）', desc: '商品・レビュー・カテゴリ・特集をベクトル化。言い換えでも意図で引ける' },
      { name: '在庫・販売可否フィルタ', desc: '在庫切れ・販売終了の商品を既定で推薦から除外（応答直前に実値を参照）' },
      { name: 'ライブ価格・セール価格の反映', desc: '埋め込み時点の通常価格ではなく、その時点の実売価格を提示' },
      { name: '販促情報の考慮', desc: 'クーポン・セットプロモ・ノベルティ・ポイントの適用可否はシステム側で判定' },
      { name: '会員ランク照会', desc: '設置タグの customer_id を名寄せし、ランク条件つき販促の案内に対応' },
    ]
  },
  {
    category: '学習・運用改善',
    icon: '📚',
    color: '#10b981',
    features: [
      { name: 'AIプロファイル管理', desc: 'トーン・応答方針・推薦ロジック・プロンプトをバージョン管理' },
      { name: 'ナレッジ・回答誘導管理', desc: '固定回答辞書でLLMより先に即答。応答速度とコストを同時に改善' },
      { name: '会話からの選択学習', desc: '良かった会話を選ぶだけで、回答本文まで下書きしたナレッジ候補を生成' },
      { name: '自動学習（既定OFF）', desc: '日次で繰り返し質問を検出して候補化。期間・件数・コスト上限を設定' },
      { name: '確からしさスコア＋ハードゲート', desc: '5軸スコアに加え、NG・矛盾・販売終了参照などは自動承認させない' },
      { name: 'Hero／バンドル／二択カード管理', desc: '押し出したい商品・セット・分岐質問をビジュアルに登録' },
    ]
  },
  {
    category: '安全性・効果測定',
    icon: '🛡️',
    color: '#3b82f6',
    features: [
      { name: 'NGワード・禁止トピック', desc: 'カテゴリ別ルール、role別検知、即ブロック・引き継ぎ判定' },
      { name: '引き継ぎ先の指定', desc: '有人オペレータ／AIスタッフを選択し、引き継ぎ対象として会話に記録' },
      { name: 'PIIマスキング', desc: '電話・メール・カード番号を会話ログから自動マスク' },
      { name: 'CV計測（注文番号・明細ベース）', desc: '重複計上を防ぎ、推薦した商品が実際に買われたかまで判定（30日ラストクリック）' },
      { name: '会話ログ・分析', desc: 'スレッド閲覧・メッセージ単位CSV・流入ページ・購入者/非購入者の比較' },
      { name: 'コンポーネント別KPI／コスト計測', desc: '表示数・CTR・CVRをUI別に集計。キャッシュヒット率とLLM費用も可視化' },
    ]
  },
]

/* ── オプション①：検索サジェスト ── */

const SUGGEST_FEATURES = [
  { name: '検索窓に1行タグ', desc: '入力のたびに低遅延で応答。プリフライトを避ける設計と人気クエリのキャッシュで検索体験を止めない' },
  { name: '3種のサジェスト', desc: 'キーワード／商品（画像・価格つき）／記事・特集ページを同時に提示' },
  { name: '形態素解析による正規化', desc: 'Sudachi で分かち書き・正規化。ひらがなだけの入力や表記ゆれでも引ける' },
  { name: '同義語辞書・手動キーワード', desc: '出したい語をピン留めして確実に上位へ。言い換えは辞書で吸収' },
  { name: '「もしかして」補正', desc: 'あいまい一致で誤字・打ち間違いを救済。補正辞書は自動生成＋手動編集' },
  { name: '意味検索フォールバック', desc: '語句一致でヒットが薄いときはベクトル検索で補完し、0件を減らす' },
  { name: 'ゼロヒットの可視化', desc: '0件だったキーワードを一覧化。品揃え・辞書の打ち手に直結させる' },
  { name: 'デバイス別デザイン設定', desc: 'PC／SPで表示件数・レイアウト・CSSを分けて調整' },
]

const SUGGEST_VALUES = [
  { num: '01', title: '検索窓は最も購買意欲が高い接点', desc: '自分から言葉を打ち込んだ人を0件で帰すのが、サイト内で最も大きな取りこぼしになる', color: '#f59e0b' },
  { num: '02', title: '辞書は運用で育つ', desc: 'ゼロヒット一覧 → 同義語・手動キーワード・補正辞書へ反映、という改善ループが管理画面上で完結する', color: '#fb923c' },
  { num: '03', title: '語彙をFindee本体と共有', desc: 'ハッシュタグ体系がサジェストの語彙にも供給される。3機能が同じ「買い手の言葉」で動く', color: '#f472b6' },
]

/* ── オプション②：Tag Trail ── */

const TAG_SOURCES = [
  { icon: '🔎', title: 'サイト内検索ワード', desc: '実際に検索された語と0件キーワード。買い手が使う言語そのもの', color: '#ec4899' },
  { icon: '📝', title: '商品名・キャッチコピー', desc: '形態素解析で頻出語を抽出。導入初期の主源泉になる', color: '#c084fc' },
  { icon: '🗂️', title: 'カテゴリ・ブランド・既存タグ', desc: '所属商品数がそのまま母集団の見込みになる、確度の高い構造化源泉', color: '#8b5cf6' },
]

const TAG_FEATURES = [
  { name: '承認キュー', desc: '日次ジョブが候補を自動投入。運営者は見込み母集団を見て承認するだけ' },
  { name: '統制語彙で一元管理', desc: '表示名はA/Bで変えられ、URLに使うスラッグは不変。表記ゆれはマッピングで吸収' },
  { name: '意味検索による母数形成', desc: 'タグ名＋同義語をベクトル検索して商品を紐づけ（目標20件・許容帯10〜40件）' },
  { name: '在庫は読み取り時フィルタ', desc: '返す瞬間に最新在庫で絞る。バッチ遅延で在庫切れをタグページに出さない' },
  { name: '3つの出口へ供給', desc: '①商品詳細のタグ表示 ②SEOタグページ ③検索サジェストの語彙' },
  { name: 'メディアコマース化', desc: 'タグページに関連記事を併載。商品の羅列ではなく回遊ハブとして機能させる' },
  { name: '週次の最適化提案', desc: '母集団が痩せたタグ・重複タグの統廃合、タグ名のA/Bを提案' },
  { name: 'awoo（nununi）互換API', desc: '呼び先のベースURLと認証情報を差し替えるだけで移行できる形で提供' },
]

const TAG_VALUES = [
  { num: '01', title: 'SEOの受け皿になる', desc: 'カテゴリでは作れない「買い手の言葉」のページが増え、検索流入の入口が広がる', color: '#ec4899' },
  { num: '02', title: 'サイト内回遊の軸になる', desc: '商品 → タグ → 商品・記事。階層をたどらずに横断で移動できる導線を作る', color: '#c084fc' },
  { num: '03', title: '未計測とゼロを混同しない', desc: '計測できていない状態を「該当者なし」と表示しない。判断を誤らせない設計を徹底', color: '#8b5cf6' },
]

const EFFECT_METRICS = [
  { label: '商品選び体験', before: 'ページ往復で疲弊', after: '会話で即発見', improvement: '体感CVR 1.4–4.4倍', color: '#6366f1' },
  { label: '検索の取りこぼし', before: '0件でそのまま離脱', after: '補正＋意味検索で救済', improvement: 'ゼロヒットを可視化', color: '#f59e0b' },
  { label: 'サイト内回遊', before: 'カテゴリ階層のみ', after: 'タグで横断回遊', improvement: '商品×記事の回遊ハブ', color: '#ec4899' },
  { label: '推薦の正確さ', before: '在庫切れ・旧価格', after: 'ライブ在庫・実売価格', improvement: 'クレームの芽を断つ', color: '#3b82f6' },
  { label: '人手対応の負荷', before: '24時間対応に限界', after: 'AIが24h即応', improvement: '人件費を戦略業務へ', color: '#8b5cf6' },
  { label: '効果測定', before: 'CVR/GMV 不透明', after: '注文番号ベースで計上', improvement: '3機能を同一定義で比較', color: '#10b981' },
]

const IMPLEMENTATION_STEPS = [
  { num: '01', title: 'ベクトル化データ取り込み', duration: '即日〜1日', desc: '商品マスタ・レビュー・カテゴリ・特集ページをW2連携で取り込み、ベクトル化。意味検索の準備完了', color: '#6366f1' },
  { num: '02', title: 'プロファイル・オープニング質問・ナレッジ', duration: '1〜3日', desc: '応答トーンをプロファイル化、ページごとの最初の設問を用意、よくある質問をナレッジ登録。NGルールも整備', color: '#8b5cf6' },
  { num: '03', title: '配信＋ウィジェット設置', duration: '即日', desc: '配信設定 → APIキー発行 → 1行スクリプト埋め込み。設置面はマークアップで自由に増やせる', color: '#a78bfa' },
  { num: '04', title: 'リッチUI設定・プリセット作成', duration: '3〜7日', desc: '使うコンポーネントを選定、Hero/バンドル/二択プリセットを作成、テーマ・自由CSSで見た目を調整', color: '#c084fc' },
  { num: '05', title: '計測タグ設置とCV確認', duration: '即日', desc: '注文完了ページの計測タグで購入を計上。Findee 単独契約でも同じタグで計測できる', color: '#e879f9' },
  { num: '06', title: 'オプション有効化（サジェスト／タグ）', duration: '1〜2週', desc: '検索窓へのタグ設置と辞書ビルド、タグ候補の承認と母集団形成。既存の商品理解をそのまま活用', color: '#f472b6' },
  { num: '07', title: 'A/Bテスト・継続改善', duration: '継続', desc: 'ON/OFF テストで導入効果を証明 → 単一要素A/Bで微調整 → 会話学習とフィードバックで常時改善', color: '#fb7185' },
]

const ROADMAP_FEATURES = [
  { icon: '🌐', title: 'SSE 2段階配信（ストリーミング）', desc: 'テキストを即時ストリーミングし、リッチUIは後追いで描画。体感速度を最大化する', priority: 'HIGH', color: '#10b981' },
  { icon: '🤝', title: 'AIスタッフへの会話引き継ぎ', desc: '接客から問合せに変わったとき、そこまでの会話をAIスタッフへ渡して聞き直しを無くす', priority: 'HIGH', color: '#6366f1' },
  { icon: '⭐', title: 'レビュー要約・ハイライト', desc: '日次バッチで全商品レビューをLLM要約。「良い点」「気になる点」を自動抽出してリッチUIで表示', priority: 'HIGH', color: '#ec4899' },
  { icon: '👤', title: 'ペルソナ連動パーソナライズ', desc: 'AIペルソナ判定（流入元・行動・購買から分類）と連動し、その人向けの推薦理由を添えて提案', priority: 'MID', color: '#8b5cf6' },
  { icon: '🔔', title: 'プロアクティブ提案', desc: 'ページ滞在・離脱予兆を検知してAIが先回り提案。「お困りですか？」を自動発火', priority: 'MID', color: '#3b82f6' },
  { icon: '🌍', title: '多言語対応（自動切替）', desc: 'ブラウザ言語／会員設定から日英中対応。配信単位で言語ポリシーを設定', priority: 'MID', color: '#c084fc' },
]

const FEATURE_TOTAL = CURRENT_FEATURES.reduce((n, c) => n + c.features.length, 0)

/* ━━━ ページ定義 ━━━ */

const SLIDES = [
  { label: '市場環境', msg: '2026年のEC接客最前線' },
  { label: '課題', msg: 'EC事業者が直面する購買支援の壁' },
  { label: 'サービス構成', msg: 'Findeeの守備範囲と、AIスタッフとの関係' },
  { label: '解決フロー', msg: 'AI × リッチUIで実現する次世代接客' },
  { label: '現行機能', msg: '今すぐ使える購買支援プラットフォーム' },
  { label: 'リッチUI戦略', msg: 'LLM Tool Use × 10コンポーネント設計' },
  { label: 'OP：検索サジェスト', msg: '検索窓の0件離脱をなくすオプション' },
  { label: 'OP：Tag Trail', msg: '買い手の言葉で回遊とSEOを作るオプション' },
  { label: '導入効果', msg: 'Before / After で見る改善インパクト' },
  { label: '導入ステップ', msg: '最短即日で始められる7ステップ' },
  { label: 'ロードマップ', msg: '開発中の次世代機能' },
]

/* ━━━ コンポーネント ━━━ */

export default function AIPurchaseAssistProposal() {
  const [activeSlide, setActiveSlide] = useState(0)
  const [exporting, setExporting] = useState(false)
  const pageRef = useRef(null)

  // PPT保存: 全ページを画像としてキャプチャしPPTに出力
  const handleExportPPT = useCallback(async () => {
    if (exporting) return
    setExporting(true)
    try {
      const pptx = new PptxGenJS()
      pptx.layout = 'LAYOUT_16x9'
      pptx.author = 'W2 Smart Hub'
      pptx.title = 'Findee Service Guide'

      const savedSlide = activeSlide
      const container = pageRef.current
      if (!container) return

      for (let i = -1; i < SLIDES.length; i++) {
        if (i >= 0) {
          setActiveSlide(i)
          await new Promise(r => setTimeout(r, 300))
        }

        const canvas = await html2canvas(container, {
          backgroundColor: '#0f172a',
          scale: 2,
          useCORS: true,
          logging: false,
        })

        const imgData = canvas.toDataURL('image/png')
        const slide = pptx.addSlide()
        slide.background = { color: '0f172a' }
        slide.addImage({
          data: imgData,
          x: 0, y: 0, w: 10, h: 5.625,
          sizing: { type: 'contain', w: 10, h: 5.625 },
        })

        if (i === -1) {
          setActiveSlide(0)
          await new Promise(r => setTimeout(r, 300))
        }
      }

      setActiveSlide(savedSlide)
      await pptx.writeFile({ fileName: 'Findee_ServiceGuide.pptx' })
    } catch (err) {
      console.error('PPT export error:', err)
      alert('PPT出力に失敗しました: ' + err.message)
    } finally {
      setExporting(false)
    }
  }, [activeSlide, exporting])

  return (
    <div style={s.page} ref={pageRef}>
      {/* ── PPT保存ボタン ── */}
      <div style={s.exportBar}>
        <button onClick={handleExportPPT} disabled={exporting} style={{ ...s.exportBtn, opacity: exporting ? 0.5 : 1 }}>
          {exporting ? '⏳ PPT出力中...' : '📥 PPTに保存'}
        </button>
      </div>

      {/* ── Hero ── */}
      <section style={s.hero}>
        <div style={s.heroBadge}>AI PURCHASE ASSIST</div>
        <h1 style={s.heroTitle}>
          <span style={s.heroAccent}>Findee</span>
          <span style={s.heroTitleSub}>Conversational Commerce Platform</span>
        </h1>
        <p style={s.heroSub}>powered by W2 Smart Hub</p>
        <p style={s.heroDesc}>
          Findee は <strong style={{ color: 'rgba(255,255,255,0.9)' }}>「接客」に特化</strong>したサービスです。<br />
          会話・検索・回遊の3つの接点で「探す・比べる・決める」を助け、購買につなげます。
        </p>

        {/* 3モジュール（コア＋オプション） */}
        <div style={s.moduleRow}>
          {MODULES.map((m, i) => (
            <div key={i} style={{ ...s.moduleCard, borderColor: `${m.color}44` }}>
              <div style={s.moduleHead}>
                <span style={{ fontSize: 20 }}>{m.icon}</span>
                <span style={{ ...s.moduleTag, background: `${m.color}22`, color: m.color }}>{m.tag}</span>
              </div>
              <div style={{ ...s.moduleName, color: m.color }}>{m.name}</div>
              <div style={s.moduleDesc}>{m.desc}</div>
            </div>
          ))}
        </div>

        <p style={s.heroNote}>
          ※ 注文・配送・返品などの<strong style={{ color: 'rgba(255,255,255,0.75)' }}>問合せ対応は「AIスタッフ」（別サービス）</strong>が担当します。
          両方をご契約いただくと、接客から問合せまでシームレスに繋がります。
        </p>
      </section>

      {/* ── ページ目次 ── */}
      <section style={s.agendaSection}>
        <h2 style={s.agendaSectionTitle}>Findee サービス紹介</h2>
        <p style={s.agendaHint}>クリックで各セクションに切り替え</p>
        <div style={s.agendaGrid}>
          {SLIDES.map((sl, i) => {
            const isActive = activeSlide === i
            return (
              <button
                key={i}
                onClick={() => setActiveSlide(i)}
                style={{ ...s.agendaCard, ...(isActive ? s.agendaCardActive : {}) }}
              >
                <div style={{ ...s.agendaNum, ...(isActive ? s.agendaNumActive : {}) }}>
                  {String(i + 1).padStart(2, '0')}
                </div>
                <div style={s.agendaBody}>
                  <div style={{ ...s.agendaLabel, ...(isActive ? s.agendaLabelActive : {}) }}>{sl.label}</div>
                  <div style={{ ...s.agendaMsg, ...(isActive ? s.agendaMsgActive : {}) }}>{sl.msg}</div>
                </div>
              </button>
            )
          })}
        </div>
      </section>

      {/* ── スライド表示 ── */}
      <div style={s.slideContainer}>
        <div style={s.sectionDivider} />
        <div style={s.sectionHeader}>
          <div style={s.sectionNum}>{String(activeSlide + 1).padStart(2, '0')}</div>
          <div>
            <h2 style={s.sectionTitle}>{SLIDES[activeSlide].label}</h2>
            <p style={s.sectionSub}>{SLIDES[activeSlide].msg}</p>
          </div>
        </div>

        {/* ─ Page 1: 市場環境 ─ */}
        {activeSlide === 0 && (
          <div>
            <h3 style={s.subHeading}>2026年、EC接客の常識が変わった</h3>
            <p style={s.subDesc}>
              チャットコマースの普及と LLM Function Calling の進化により、「商品ページを見て買う」から「AIに相談して買う」への移行が加速。
              同時に、接客AIと問合せ対応AIを役割で分け、必要なときだけ繋ぐ設計が主流になりつつあります。
            </p>

            <div style={s.statGrid}>
              {MARKET_STATS.map((st, i) => (
                <div key={i} style={s.statCard}>
                  <div style={s.statNum}>{st.num}</div>
                  <div style={s.statLabel}>{st.label}</div>
                  <div style={s.statSrc}>{st.src}</div>
                </div>
              ))}
            </div>

            <h3 style={{ ...s.subHeading, marginTop: 28 }}>押さえるべき4大トレンド</h3>
            <div style={s.trendGrid}>
              {TRENDS.map((t, i) => (
                <div key={i} style={s.trendCard}>
                  <div style={{ ...s.trendIcon, background: `${t.color}22`, color: t.color }}>{t.icon}</div>
                  <div>
                    <div style={{ ...s.trendTitle, color: t.color }}>{t.title}</div>
                    <div style={s.trendDesc}>{t.desc}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ─ Page 2: 課題 ─ */}
        {activeSlide === 1 && (
          <div>
            <h3 style={s.subHeading}>EC事業者の購買支援、6つの壁</h3>
            <p style={s.subDesc}>
              多くのEC事業者がAI接客の重要性を認識しつつも、以下の課題により本格運用に至っていません。
            </p>
            <div style={s.painGrid}>
              {PAIN_POINTS.map((p, i) => (
                <div key={i} style={s.painCard}>
                  <div style={{ ...s.painDot, background: p.color }} />
                  <div>
                    <div style={s.painTitle}>{p.problem}</div>
                    <div style={s.painDesc}>{p.impact}</div>
                  </div>
                </div>
              ))}
            </div>
            <div style={s.alertBanner}>
              <div style={s.alertIcon}>⚠️</div>
              <p style={s.alertText}>
                チャットだけを入れても、<strong style={{ color: '#fbbf24' }}>検索窓と回遊導線が旧来のまま</strong>では取りこぼしは残ります。
                Findee は「会話・検索・回遊」の3接点をひとつの語彙と計測基準で揃えることで、入口から出口までの離脱を潰します。
              </p>
            </div>
          </div>
        )}

        {/* ─ Page 3: サービス構成 ─ */}
        {activeSlide === 2 && (
          <div>
            <h3 style={s.subHeading}>Findee は「接客」に特化しています</h3>
            <p style={s.subDesc}>
              商品を選ぶまでを助けるのが Findee、注文後の困りごとを解くのが AIスタッフ（別サービス）です。
              必要な知識も責任範囲も違うため、無理に1体のAIへ統合せず、役割を分けたうえで繋ぐ設計にしています。
            </p>

            <div style={s.scopeGrid}>
              {SCOPE_COMPARE.map((sc, i) => (
                <div key={i} style={{ ...s.scopeCard, borderColor: `${sc.color}44`, background: sc.key === 'findee' ? 'rgba(99,102,241,0.07)' : 'rgba(255,255,255,0.03)' }}>
                  <div style={s.scopeHead}>
                    <span style={{ fontSize: 24 }}>{sc.icon}</span>
                    <span style={{ ...s.scopeBadge, background: `${sc.color}22`, color: sc.color }}>{sc.badge}</span>
                  </div>
                  <div style={{ ...s.scopeTitle, color: sc.color }}>{sc.title}</div>
                  <div style={s.scopeList}>
                    {sc.items.map((it, j) => (
                      <div key={j} style={s.scopeItem}>
                        <span style={{ color: sc.color, flexShrink: 0 }}>▸</span>
                        <span>{it}</span>
                      </div>
                    ))}
                  </div>
                  <div style={s.scopeExample}>
                    <div style={s.scopeExampleLabel}>想定される発話</div>
                    <div style={s.scopeExampleText}>{sc.example}</div>
                  </div>
                </div>
              ))}
            </div>

            <div style={{ ...s.alertBanner, marginTop: 20, marginBottom: 24 }}>
              <div style={s.alertIcon}>🔗</div>
              <p style={s.alertText}>
                <strong style={{ color: '#fbbf24' }}>両方をご契約いただくと、接客と問合せがシームレスに繋がります。</strong><br />
                エンドユーザーから見れば会話の入口はひとつ。内部で適切な担当へ渡すため、聞き先を選ばせません。
              </p>
            </div>

            <h3 style={s.subHeading}>併用時に効く4つの連携</h3>
            <div style={s.seamlessGrid}>
              {SEAMLESS_ITEMS.map((si, i) => (
                <div key={i} style={{ ...s.seamlessCard, borderColor: `${si.color}33` }}>
                  <div style={s.seamlessHead}>
                    <span style={{ fontSize: 18 }}>{si.icon}</span>
                    <span style={s.seamlessTitle}>{si.title}</span>
                    <span style={{ ...s.seamlessStatus, background: `${si.color}1f`, color: si.color }}>{si.status}</span>
                  </div>
                  <p style={s.seamlessDesc}>{si.desc}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ─ Page 4: 解決フロー ─ */}
        {activeSlide === 3 && (
          <div>
            <h3 style={s.subHeading}>ワンストップ運用フロー</h3>
            <p style={s.subDesc}>
              配信設定からオープニング質問、プリセット登録、リッチUI 自動表示、引き継ぎ判定、A/Bテストまでを一気通貫で。
              AI動的生成 × 加盟店プリセットを両立させ、購買意欲を最大化します。
            </p>
            <div style={s.workflowTimeline}>
              {WORKFLOW_STEPS.map((ws, i) => (
                <div key={i} style={s.workflowItem}>
                  <div style={s.workflowLeft}>
                    <div style={{ ...s.workflowNum, background: ws.color }}>{ws.num}</div>
                    {i < WORKFLOW_STEPS.length - 1 && <div style={{ ...s.workflowLine, background: `linear-gradient(180deg, ${ws.color}, ${WORKFLOW_STEPS[i + 1].color})` }} />}
                  </div>
                  <div style={{ ...s.workflowContent, borderColor: `${ws.color}33` }}>
                    <div style={s.workflowHeader}>
                      <span style={s.workflowIcon}>{ws.icon}</span>
                      <span style={{ ...s.workflowTitle, color: ws.color }}>{ws.title}</span>
                    </div>
                    <p style={s.workflowDesc}>{ws.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ─ Page 5: 現行機能 ─ */}
        {activeSlide === 4 && (
          <div>
            <h3 style={s.subHeading}>今すぐ利用可能な機能群</h3>
            <p style={s.subDesc}>
              設置・対話・入口設計・推薦精度・学習・安全測定の6カテゴリ・{FEATURE_TOTAL}機能で、Findee の接客を多角的に支えます。
            </p>
            <div style={s.featureCategoryGrid}>
              {CURRENT_FEATURES.map((cat, ci) => (
                <div key={ci} style={{ ...s.featureCategoryCard, borderColor: `${cat.color}33` }}>
                  <div style={s.featureCategoryHeader}>
                    <span style={{ fontSize: 22 }}>{cat.icon}</span>
                    <span style={{ ...s.featureCategoryTitle, color: cat.color }}>{cat.category}</span>
                  </div>
                  <div style={s.featureList}>
                    {cat.features.map((f, fi) => (
                      <div key={fi} style={s.featureItem}>
                        <div style={{ ...s.featureCheck, color: cat.color }}>✓</div>
                        <div>
                          <div style={s.featureName}>{f.name}</div>
                          <div style={s.featureDesc}>{f.desc}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ─ Page 6: リッチUI戦略 ─ */}
        {activeSlide === 5 && (
          <div>
            <h3 style={s.subHeading}>LLM Tool Use × 10コンポーネント設計</h3>
            <p style={s.subDesc}>
              Anthropic Claude の Tool Use（Function Calling）で、AIが会話文脈を読んで最適なリッチUIを自動選択。
              加盟店が手動で押し出したい商品はプリセット強制発火で確実に表示し、両方を共存させた設計を実現します。
            </p>

            <div style={s.geoFlowWrap}>
              <div style={s.geoFlowTitle}>会話 × リッチUI 自動選択フロー</div>
              <div style={s.geoFlow}>
                {[
                  { icon: '💬', label: 'ユーザー発話', sub: '「通勤コートを\nおすすめして」' },
                  { icon: '🔥', label: 'プリセット強制発火', sub: 'キーワードヒットで\nLLM呼出前に確定' },
                  { icon: '🤖', label: 'LLM Tool Use', sub: '文脈を読んで\n最適UIを選択' },
                  { icon: '🧾', label: 'ライブ属性の照合', sub: '在庫・価格・販促を\n応答直前に引当' },
                  { icon: '🎨', label: 'リッチUI描画', sub: '10コンポーネントで\n動的レンダリング' },
                  { icon: '📊', label: 'KPI計測', sub: '表示/CTR/CVR を\n自動収集' },
                ].map((gf, i) => (
                  <React.Fragment key={i}>
                    {i > 0 && <div style={s.geoFlowArrow}>▶</div>}
                    <div style={s.geoFlowStep}>
                      <div style={s.geoFlowIcon}>{gf.icon}</div>
                      <div style={s.geoFlowLabel}>{gf.label}</div>
                      <div style={s.geoFlowSub}>{gf.sub}</div>
                    </div>
                  </React.Fragment>
                ))}
              </div>
            </div>

            <div style={s.geoValueGrid}>
              {[
                { num: '01', title: '10種コンポーネントで体験を最適化', desc: 'Gallery / Hero / 比較表 / Radar / Personalized / 二択 / Filter / Reviews / B&A / Bundle を文脈で出し分け', color: '#6366f1' },
                { num: '02', title: 'プリセット × LLM のハイブリッド', desc: '加盟店が押し出したい商品はプリセットで確実表示、それ以外は AI が動的選択。両立できる設計', color: '#ec4899' },
                { num: '03', title: '「事実」はLLMに判断させない', desc: '在庫・価格・販促の適用可否はシステム側で判定し、LLM には判定済みの結果と表示文言のみを渡す', color: '#10b981' },
              ].map((gv, i) => (
                <div key={i} style={{ ...s.geoValueCard, borderColor: `${gv.color}33` }}>
                  <div style={{ ...s.geoValueNum, color: gv.color }}>{gv.num}</div>
                  <div style={{ ...s.geoValueTitle, color: gv.color }}>{gv.title}</div>
                  <div style={s.geoValueDesc}>{gv.desc}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ─ Page 7: オプション①検索サジェスト ─ */}
        {activeSlide === 6 && (
          <div>
            <div style={{ ...s.optBanner, borderColor: 'rgba(245,158,11,0.3)', background: 'linear-gradient(135deg, rgba(245,158,11,0.12) 0%, rgba(236,72,153,0.06) 100%)' }}>
              <div style={s.optBannerIcon}>🔍</div>
              <div>
                <div style={s.optBannerTagRow}>
                  <span style={{ ...s.optBannerTag, background: 'rgba(245,158,11,0.18)', color: '#fbbf24' }}>Findee オプション</span>
                </div>
                <div style={{ ...s.optBannerTitle, color: '#fbbf24' }}>検索サジェスト</div>
                <p style={s.optBannerDesc}>
                  検索窓に1行のタグを設置するだけ。入力の途中から候補を返し、「見つからない」で離れていた人を拾い直します。
                </p>
              </div>
            </div>

            <h3 style={s.subHeading}>検索窓での体験</h3>
            <div style={s.geoFlowWrap}>
              <div style={s.geoFlow}>
                {[
                  { icon: '⌨️', label: '入力', sub: '「はっすい」と\nひらがなで入力' },
                  { icon: '🈁', label: '正規化', sub: '形態素解析で\n表記ゆれを吸収' },
                  { icon: '🔤', label: '候補提示', sub: 'キーワード／商品／\n記事を同時に表示' },
                  { icon: '💡', label: 'もしかして', sub: '誤字は補正辞書で\n言い直しを提案' },
                  { icon: '🧭', label: '意味検索FB', sub: 'ヒットが薄いときは\nベクトルで補完' },
                  { icon: '📈', label: '計測', sub: '表示/クリック/CVを\n注文番号ベースで' },
                ].map((gf, i) => (
                  <React.Fragment key={i}>
                    {i > 0 && <div style={s.geoFlowArrow}>▶</div>}
                    <div style={s.geoFlowStep}>
                      <div style={{ ...s.geoFlowIcon, background: 'rgba(245,158,11,0.12)', borderColor: 'rgba(245,158,11,0.25)' }}>{gf.icon}</div>
                      <div style={s.geoFlowLabel}>{gf.label}</div>
                      <div style={s.geoFlowSub}>{gf.sub}</div>
                    </div>
                  </React.Fragment>
                ))}
              </div>
            </div>

            <h3 style={s.subHeading}>主な機能</h3>
            <div style={s.optFeatureGrid}>
              {SUGGEST_FEATURES.map((f, i) => (
                <div key={i} style={{ ...s.optFeatureCard, borderColor: 'rgba(245,158,11,0.22)' }}>
                  <div style={{ ...s.optFeatureName, color: '#fbbf24' }}>{f.name}</div>
                  <div style={s.optFeatureDesc}>{f.desc}</div>
                </div>
              ))}
            </div>

            <h3 style={{ ...s.subHeading, marginTop: 24 }}>導入価値</h3>
            <div style={s.geoValueGrid}>
              {SUGGEST_VALUES.map((gv, i) => (
                <div key={i} style={{ ...s.geoValueCard, borderColor: `${gv.color}33` }}>
                  <div style={{ ...s.geoValueNum, color: gv.color }}>{gv.num}</div>
                  <div style={{ ...s.geoValueTitle, color: gv.color }}>{gv.title}</div>
                  <div style={s.geoValueDesc}>{gv.desc}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ─ Page 8: オプション②Tag Trail ─ */}
        {activeSlide === 7 && (
          <div>
            <div style={{ ...s.optBanner, borderColor: 'rgba(236,72,153,0.3)', background: 'linear-gradient(135deg, rgba(236,72,153,0.12) 0%, rgba(139,92,246,0.06) 100%)' }}>
              <div style={s.optBannerIcon}>#️⃣</div>
              <div>
                <div style={s.optBannerTagRow}>
                  <span style={{ ...s.optBannerTag, background: 'rgba(236,72,153,0.18)', color: '#f472b6' }}>Findee オプション</span>
                </div>
                <div style={{ ...s.optBannerTitle, color: '#f472b6' }}>Tag Trail</div>
                <p style={s.optBannerDesc}>
                  「敏感肌向け」「ギフト」のような買い手の言葉でタグ体系を作り、商品詳細・SEOタグページ・検索サジェストの3つの出口へ供給します。
                </p>
              </div>
            </div>

            <h3 style={s.subHeading}>見出し語は3つの源泉から自動で集まる</h3>
            <div style={s.tagSourceGrid}>
              {TAG_SOURCES.map((ts, i) => (
                <div key={i} style={{ ...s.tagSourceCard, borderColor: `${ts.color}33` }}>
                  <div style={{ ...s.trendIcon, background: `${ts.color}22`, color: ts.color, marginBottom: 10 }}>{ts.icon}</div>
                  <div style={{ ...s.tagSourceTitle, color: ts.color }}>{ts.title}</div>
                  <div style={s.tagSourceDesc}>{ts.desc}</div>
                </div>
              ))}
            </div>

            <div style={s.geoFlowWrap}>
              <div style={s.geoFlowTitle}>タグ体系は1つ、消費先は3つ</div>
              <div style={s.geoFlow}>
                {[
                  { icon: '📥', label: '候補抽出', sub: '日次ジョブが\n承認キューへ投入' },
                  { icon: '✅', label: '承認', sub: '見込み母集団を見て\nタグ化を確定' },
                  { icon: '🧲', label: '母集団形成', sub: '意味検索で商品を紐づけ\n（目標20件）' },
                  { icon: '🚚', label: '3つの出口', sub: '商品詳細／タグページ／\nサジェスト語彙' },
                  { icon: '🔁', label: '週次最適化', sub: '統廃合・タグ名A/B・\n母集団の補正' },
                ].map((gf, i) => (
                  <React.Fragment key={i}>
                    {i > 0 && <div style={s.geoFlowArrow}>▶</div>}
                    <div style={s.geoFlowStep}>
                      <div style={{ ...s.geoFlowIcon, background: 'rgba(236,72,153,0.12)', borderColor: 'rgba(236,72,153,0.25)' }}>{gf.icon}</div>
                      <div style={s.geoFlowLabel}>{gf.label}</div>
                      <div style={s.geoFlowSub}>{gf.sub}</div>
                    </div>
                  </React.Fragment>
                ))}
              </div>
            </div>

            <h3 style={s.subHeading}>主な機能</h3>
            <div style={s.optFeatureGrid}>
              {TAG_FEATURES.map((f, i) => (
                <div key={i} style={{ ...s.optFeatureCard, borderColor: 'rgba(236,72,153,0.22)' }}>
                  <div style={{ ...s.optFeatureName, color: '#f472b6' }}>{f.name}</div>
                  <div style={s.optFeatureDesc}>{f.desc}</div>
                </div>
              ))}
            </div>

            <h3 style={{ ...s.subHeading, marginTop: 24 }}>導入価値</h3>
            <div style={s.geoValueGrid}>
              {TAG_VALUES.map((gv, i) => (
                <div key={i} style={{ ...s.geoValueCard, borderColor: `${gv.color}33` }}>
                  <div style={{ ...s.geoValueNum, color: gv.color }}>{gv.num}</div>
                  <div style={{ ...s.geoValueTitle, color: gv.color }}>{gv.title}</div>
                  <div style={s.geoValueDesc}>{gv.desc}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ─ Page 9: 導入効果 ─ */}
        {activeSlide === 8 && (
          <div>
            <h3 style={s.subHeading}>Before / After ─ 導入効果</h3>
            <p style={s.subDesc}>
              会話・検索・回遊の3接点をひとつの語彙と計測基準で揃えることで、購買体験のあらゆる指標が改善します。
            </p>
            <div style={s.effectGrid}>
              {EFFECT_METRICS.map((em, i) => (
                <div key={i} style={{ ...s.effectCard, borderColor: `${em.color}33` }}>
                  <div style={{ ...s.effectLabel, color: em.color }}>{em.label}</div>
                  <div style={s.effectRow}>
                    <div style={s.effectBefore}>
                      <div style={s.effectBeforeLabel}>Before</div>
                      <div style={s.effectBeforeValue}>{em.before}</div>
                    </div>
                    <div style={s.effectArrow}>→</div>
                    <div style={s.effectAfter}>
                      <div style={s.effectAfterLabel}>After</div>
                      <div style={{ ...s.effectAfterValue, color: em.color }}>{em.after}</div>
                    </div>
                  </div>
                  <div style={{ ...s.effectImprovement, background: `${em.color}15`, color: em.color }}>{em.improvement}</div>
                </div>
              ))}
            </div>

            <div style={s.roiBanner}>
              <div style={s.roiIcon}>💰</div>
              <div>
                <div style={s.roiTitle}>ROI試算</div>
                <p style={s.roiText}>
                  ナレッジ・プリセット即答化で <strong>LLM呼び出し30〜50%削減</strong> → コスト最適化<br />
                  リッチUI連動で <strong style={{ color: '#10b981', fontSize: 18 }}>体感CVR 1.4〜4.4倍</strong>（外部レポート参考値）<br />
                  オプション併用で、<strong>検索の0件離脱</strong>と<strong>回遊の行き止まり</strong>という残りの取りこぼしまで回収<br />
                  人手接客の負荷を <strong>戦略業務へシフト</strong>、24時間無人で同等の購買支援が可能
                </p>
              </div>
            </div>
          </div>
        )}

        {/* ─ Page 10: 導入ステップ ─ */}
        {activeSlide === 9 && (
          <div>
            <h3 style={s.subHeading}>最短即日で始められる7ステップ</h3>
            <p style={s.subDesc}>
              W2 Smart Hub の管理画面から、特別な開発不要で AI 接客を導入できます。
              オプション（検索サジェスト／Tag Trail）は後から追加でき、取り込み済みの商品データをそのまま使えます。
            </p>
            <div style={s.implTimeline}>
              {IMPLEMENTATION_STEPS.map((ist, i) => (
                <div key={i} style={s.implItem}>
                  <div style={s.implLeft}>
                    <div style={{ ...s.implNum, background: `linear-gradient(135deg, ${ist.color}, ${ist.color}cc)` }}>{ist.num}</div>
                    {i < IMPLEMENTATION_STEPS.length - 1 && <div style={s.implLine} />}
                  </div>
                  <div style={s.implContent}>
                    <div style={s.implHeader}>
                      <span style={{ ...s.implTitle, color: ist.color }}>{ist.title}</span>
                      <span style={s.implDuration}>{ist.duration}</span>
                    </div>
                    <p style={s.implDesc}>{ist.desc}</p>
                  </div>
                </div>
              ))}
            </div>

            <div style={s.ctaBanner}>
              <div style={s.ctaText}>
                <strong style={{ fontSize: 16, color: '#fff' }}>まずはベクトル化＆配信設定から</strong><br />
                <span style={{ fontSize: 13, color: 'rgba(255,255,255,0.7)' }}>商品マスタを連携するだけで、AIがその場から相談に乗れる状態になります</span>
              </div>
            </div>
          </div>
        )}

        {/* ─ Page 11: ロードマップ ─ */}
        {activeSlide === 10 && (
          <div>
            <h3 style={s.subHeading}>開発ロードマップ ─ 次世代機能</h3>
            <p style={s.subDesc}>
              体感速度の向上、AIスタッフとの会話引き継ぎ、ペルソナ連動のパーソナライズなどを順次リリース予定です。
            </p>
            <div style={s.roadmapGrid}>
              {ROADMAP_FEATURES.map((rf, i) => (
                <div key={i} style={{ ...s.roadmapCard, borderColor: `${rf.color}33` }}>
                  <div style={s.roadmapCardHeader}>
                    <span style={{ fontSize: 20 }}>{rf.icon}</span>
                    <span style={{ ...s.roadmapPriority, ...priorityStyle(rf.priority) }}>
                      {rf.priority}
                    </span>
                  </div>
                  <div style={{ ...s.roadmapTitle, color: rf.color }}>{rf.title}</div>
                  <div style={s.roadmapDesc}>{rf.desc}</div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* ── フッター ── */}
      <div style={s.footer}>
        <p style={s.footerText}>Findee &mdash; AI Purchase Assist, powered by W2 Smart Hub</p>
        {/* ログイン中のみ閲覧可能な Findee 体験デモへの導線（控えめに配置） */}
        <a
          href="/findee-demo.html"
          target="_blank"
          rel="noopener noreferrer"
          style={s.demoLink}
          onMouseEnter={(e) => (e.currentTarget.style.color = 'rgba(255,255,255,0.6)')}
          onMouseLeave={(e) => (e.currentTarget.style.color = 'rgba(255,255,255,0.12)')}
        >
          Findee 体験デモ
        </a>
      </div>
    </div>
  )
}

/* 優先度バッジの配色（HIGH / MID / LOW） */
function priorityStyle(priority) {
  if (priority === 'HIGH') return { background: 'rgba(239,68,68,0.15)', color: '#ef4444' }
  if (priority === 'MID') return { background: 'rgba(245,158,11,0.15)', color: '#f59e0b' }
  return { background: 'rgba(148,163,184,0.15)', color: '#94a3b8' }
}

/* ━━━ スタイル定義 ━━━ */

const s = {
  page: { maxWidth: 960, margin: '0 auto', padding: '0 0 40px', position: 'relative' },

  exportBar: { display: 'flex', justifyContent: 'flex-end', marginBottom: 12 },
  exportBtn: {
    padding: '8px 20px', borderRadius: 10, border: '1px solid rgba(99,102,241,0.3)',
    background: 'rgba(99,102,241,0.1)', color: '#a5b4fc',
    fontSize: 13, fontWeight: 600, cursor: 'pointer', transition: 'all 0.2s',
  },

  hero: {
    textAlign: 'center', padding: '48px 32px 32px',
    background: 'linear-gradient(160deg, rgba(99,102,241,0.10) 0%, rgba(139,92,246,0.10) 50%, rgba(236,72,153,0.06) 100%)',
    borderRadius: 24, border: '1px solid rgba(99,102,241,0.15)', marginBottom: 32,
  },
  heroBadge: {
    display: 'inline-block', padding: '4px 18px', borderRadius: 20,
    fontSize: 11, fontWeight: 700, letterSpacing: '0.12em',
    background: 'rgba(99,102,241,0.15)', color: '#a5b4fc', marginBottom: 16,
  },
  heroTitle: { fontSize: 36, fontWeight: 800, color: '#fff', margin: '0 0 2px', letterSpacing: '-0.02em' },
  heroAccent: {
    background: 'linear-gradient(135deg, #818cf8 0%, #c084fc 50%, #f472b6 100%)',
    WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
  },
  heroTitleSub: {
    display: 'block', fontSize: '0.42em', fontWeight: 500, marginTop: 6,
    color: 'rgba(255,255,255,0.55)', letterSpacing: '0.08em',
  },
  heroSub: { fontSize: 13, fontWeight: 500, color: 'rgba(255,255,255,0.35)', margin: '0 0 18px', letterSpacing: '0.06em' },
  heroDesc: { fontSize: 15, color: 'rgba(255,255,255,0.7)', lineHeight: 1.8, margin: '0 0 24px' },
  heroNote: {
    fontSize: 12, color: 'rgba(255,255,255,0.5)', lineHeight: 1.8, margin: '20px 0 0',
    padding: '12px 18px', borderRadius: 12,
    background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)',
  },

  moduleRow: { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 },
  moduleCard: {
    padding: '16px 18px', borderRadius: 16, textAlign: 'left',
    background: 'rgba(255,255,255,0.05)', border: '1px solid',
  },
  moduleHead: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 },
  moduleTag: {
    padding: '2px 10px', borderRadius: 10, fontSize: 9, fontWeight: 800, letterSpacing: '0.08em',
  },
  moduleName: { fontSize: 13, fontWeight: 700, marginBottom: 6, lineHeight: 1.4 },
  moduleDesc: { fontSize: 11, color: 'rgba(255,255,255,0.5)', lineHeight: 1.6 },

  agendaSection: { marginBottom: 32 },
  agendaSectionTitle: { fontSize: 20, fontWeight: 700, color: 'rgba(255,255,255,0.85)', textAlign: 'center', margin: '0 0 20px' },
  agendaHint: { textAlign: 'center', fontSize: 13, color: 'rgba(255,255,255,0.35)', margin: '0 0 18px' },
  agendaGrid: { display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10 },
  agendaCard: {
    display: 'flex', alignItems: 'center', gap: 12, padding: '14px 16px', borderRadius: 14,
    border: '1px solid rgba(226,232,240,0.2)', background: 'rgba(255,255,255,0.92)',
    cursor: 'pointer', transition: 'all 0.25s', textAlign: 'left', boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
  },
  agendaCardActive: {
    background: 'linear-gradient(135deg, #6366f1 0%, #c084fc 100%)',
    border: '1px solid rgba(99,102,241,0.5)', boxShadow: '0 8px 30px rgba(99,102,241,0.3)', transform: 'scale(1.02)',
  },
  agendaNum: {
    width: 40, height: 40, borderRadius: 12,
    background: 'linear-gradient(135deg, #ddd6fe 0%, #c4b5fd 100%)', color: '#6d28d9',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: 16, fontWeight: 900, flexShrink: 0,
  },
  agendaNumActive: { background: 'rgba(255,255,255,0.2)', color: '#fff' },
  agendaBody: { flex: 1, minWidth: 0 },
  agendaLabel: { fontSize: 13, fontWeight: 700, color: '#1e293b', marginBottom: 2 },
  agendaLabelActive: { color: '#fff' },
  agendaMsg: { fontSize: 10, color: '#64748b', lineHeight: 1.4 },
  agendaMsgActive: { color: 'rgba(255,255,255,0.75)' },

  slideContainer: { minHeight: 400 },
  sectionDivider: {
    height: 1, marginBottom: 32,
    background: 'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.15) 30%, rgba(255,255,255,0.15) 70%, transparent 100%)',
  },
  sectionHeader: { display: 'flex', alignItems: 'flex-start', gap: 18, marginBottom: 28 },
  sectionNum: {
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    width: 44, height: 44, borderRadius: 14,
    background: 'linear-gradient(135deg, #6366f1 0%, #c084fc 100%)',
    color: '#fff', fontSize: 16, fontWeight: 800, flexShrink: 0,
  },
  sectionTitle: { fontSize: 24, fontWeight: 700, color: 'rgba(255,255,255,0.9)', margin: '0 0 4px' },
  sectionSub: { fontSize: 14, color: 'rgba(255,255,255,0.45)', margin: 0 },
  subHeading: { fontSize: 16, fontWeight: 700, color: 'rgba(255,255,255,0.75)', margin: '0 0 12px', paddingLeft: 2 },
  subDesc: { fontSize: 13, color: 'rgba(255,255,255,0.45)', margin: '0 0 20px', lineHeight: 1.7 },

  statGrid: { display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 8 },
  statCard: {
    padding: '22px 16px', borderRadius: 16, textAlign: 'center',
    background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)',
  },
  statNum: {
    fontSize: 32, fontWeight: 900, lineHeight: 1,
    background: 'linear-gradient(135deg, #818cf8 0%, #c084fc 100%)',
    WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', marginBottom: 8,
  },
  statLabel: { fontSize: 12, fontWeight: 600, color: 'rgba(255,255,255,0.7)', marginBottom: 6, lineHeight: 1.4 },
  statSrc: { fontSize: 10, color: 'rgba(255,255,255,0.3)' },
  trendGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 },
  trendCard: {
    display: 'flex', gap: 14, padding: '20px', borderRadius: 16,
    background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)',
  },
  trendIcon: {
    width: 44, height: 44, borderRadius: 12, display: 'flex', alignItems: 'center',
    justifyContent: 'center', fontSize: 22, flexShrink: 0,
  },
  trendTitle: { fontSize: 14, fontWeight: 700, marginBottom: 6 },
  trendDesc: { fontSize: 12, color: 'rgba(255,255,255,0.5)', lineHeight: 1.6 },

  painGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 20 },
  painCard: {
    display: 'flex', alignItems: 'flex-start', gap: 14, padding: '20px 22px',
    background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 16,
  },
  painDot: { width: 10, height: 10, borderRadius: '50%', flexShrink: 0, marginTop: 5 },
  painTitle: { fontSize: 14, fontWeight: 700, color: 'rgba(255,255,255,0.85)', margin: '0 0 6px' },
  painDesc: { fontSize: 12, color: 'rgba(255,255,255,0.5)', margin: 0, lineHeight: 1.6 },
  alertBanner: {
    display: 'flex', alignItems: 'center', gap: 16, padding: '18px 24px',
    background: 'linear-gradient(135deg, rgba(245,158,11,0.1) 0%, rgba(239,68,68,0.06) 100%)',
    border: '1px solid rgba(245,158,11,0.2)', borderRadius: 16,
  },
  alertIcon: { fontSize: 24, flexShrink: 0 },
  alertText: { fontSize: 13, color: 'rgba(255,255,255,0.7)', margin: 0, lineHeight: 1.7 },

  /* サービス構成（棲み分け） */
  scopeGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 },
  scopeCard: { padding: '22px', borderRadius: 18, border: '1px solid' },
  scopeHead: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 },
  scopeBadge: { padding: '3px 12px', borderRadius: 10, fontSize: 10, fontWeight: 800, letterSpacing: '0.06em' },
  scopeTitle: { fontSize: 15, fontWeight: 700, marginBottom: 14, lineHeight: 1.4 },
  scopeList: { display: 'flex', flexDirection: 'column', gap: 8 },
  scopeItem: { display: 'flex', gap: 8, fontSize: 12, color: 'rgba(255,255,255,0.6)', lineHeight: 1.6 },
  scopeExample: {
    marginTop: 16, padding: '12px 14px', borderRadius: 12,
    background: 'rgba(0,0,0,0.22)', border: '1px solid rgba(255,255,255,0.06)',
  },
  scopeExampleLabel: { fontSize: 10, color: 'rgba(255,255,255,0.35)', marginBottom: 6, letterSpacing: '0.06em' },
  scopeExampleText: { fontSize: 11, color: 'rgba(255,255,255,0.55)', lineHeight: 1.8, whiteSpace: 'pre-line' },

  seamlessGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 },
  seamlessCard: {
    padding: '18px 20px', borderRadius: 16,
    background: 'rgba(255,255,255,0.04)', border: '1px solid',
  },
  seamlessHead: { display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8, flexWrap: 'wrap' },
  seamlessTitle: { fontSize: 13, fontWeight: 700, color: 'rgba(255,255,255,0.85)' },
  seamlessStatus: { padding: '2px 10px', borderRadius: 10, fontSize: 10, fontWeight: 700 },
  seamlessDesc: { fontSize: 12, color: 'rgba(255,255,255,0.5)', margin: 0, lineHeight: 1.7 },

  workflowTimeline: { display: 'flex', flexDirection: 'column', gap: 0 },
  workflowItem: { display: 'flex', gap: 16 },
  workflowLeft: { display: 'flex', flexDirection: 'column', alignItems: 'center', width: 44, flexShrink: 0 },
  workflowNum: {
    width: 44, height: 44, borderRadius: 22, display: 'flex', alignItems: 'center',
    justifyContent: 'center', color: '#fff', fontSize: 14, fontWeight: 800,
  },
  workflowLine: { width: 2, flex: 1, minHeight: 20, opacity: 0.4 },
  workflowContent: {
    flex: 1, padding: '16px 20px', marginBottom: 12, borderRadius: 16,
    background: 'rgba(255,255,255,0.04)', border: '1px solid',
  },
  workflowHeader: { display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 },
  workflowIcon: { fontSize: 20 },
  workflowTitle: { fontSize: 15, fontWeight: 700 },
  workflowDesc: { fontSize: 12, color: 'rgba(255,255,255,0.55)', margin: 0, lineHeight: 1.7 },

  featureCategoryGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 },
  featureCategoryCard: {
    padding: '22px', borderRadius: 18,
    background: 'rgba(255,255,255,0.04)', border: '1px solid',
  },
  featureCategoryHeader: { display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 },
  featureCategoryTitle: { fontSize: 15, fontWeight: 700 },
  featureList: { display: 'flex', flexDirection: 'column', gap: 12 },
  featureItem: { display: 'flex', gap: 10 },
  featureCheck: { fontSize: 14, fontWeight: 800, flexShrink: 0, marginTop: 1 },
  featureName: { fontSize: 13, fontWeight: 600, color: 'rgba(255,255,255,0.8)', marginBottom: 2 },
  featureDesc: { fontSize: 11, color: 'rgba(255,255,255,0.4)', lineHeight: 1.4 },

  /* オプション紹介 */
  optBanner: {
    display: 'flex', gap: 18, padding: '22px 26px', borderRadius: 18,
    border: '1px solid', marginBottom: 26,
  },
  optBannerIcon: { fontSize: 34, flexShrink: 0, lineHeight: 1.2 },
  optBannerTagRow: { marginBottom: 6 },
  optBannerTag: { display: 'inline-block', padding: '3px 12px', borderRadius: 10, fontSize: 10, fontWeight: 800, letterSpacing: '0.06em' },
  optBannerTitle: { fontSize: 20, fontWeight: 800, marginBottom: 8 },
  optBannerDesc: { fontSize: 13, color: 'rgba(255,255,255,0.65)', margin: 0, lineHeight: 1.8 },
  optFeatureGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 },
  optFeatureCard: {
    padding: '16px 18px', borderRadius: 14,
    background: 'rgba(255,255,255,0.04)', border: '1px solid',
  },
  optFeatureName: { fontSize: 13, fontWeight: 700, marginBottom: 6 },
  optFeatureDesc: { fontSize: 11, color: 'rgba(255,255,255,0.5)', lineHeight: 1.6 },

  tagSourceGrid: { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 14, marginBottom: 24 },
  tagSourceCard: {
    padding: '20px', borderRadius: 16,
    background: 'rgba(255,255,255,0.04)', border: '1px solid',
  },
  tagSourceTitle: { fontSize: 14, fontWeight: 700, marginBottom: 6 },
  tagSourceDesc: { fontSize: 12, color: 'rgba(255,255,255,0.5)', lineHeight: 1.6 },

  roadmapGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 },
  roadmapCard: {
    padding: '20px', borderRadius: 16,
    background: 'rgba(255,255,255,0.04)', border: '1px solid',
  },
  roadmapCardHeader: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 },
  roadmapPriority: { padding: '2px 10px', borderRadius: 10, fontSize: 10, fontWeight: 700, letterSpacing: '0.05em' },
  roadmapTitle: { fontSize: 14, fontWeight: 700, marginBottom: 8 },
  roadmapDesc: { fontSize: 12, color: 'rgba(255,255,255,0.5)', lineHeight: 1.6, margin: 0 },

  effectGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 20 },
  effectCard: {
    padding: '22px', borderRadius: 18,
    background: 'rgba(255,255,255,0.04)', border: '1px solid', textAlign: 'center',
  },
  effectLabel: { fontSize: 13, fontWeight: 700, marginBottom: 16 },
  effectRow: { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12, marginBottom: 14 },
  effectBefore: {},
  effectBeforeLabel: { fontSize: 10, color: 'rgba(255,255,255,0.35)', marginBottom: 4 },
  effectBeforeValue: { fontSize: 14, fontWeight: 600, color: 'rgba(255,255,255,0.5)' },
  effectArrow: { fontSize: 20, color: 'rgba(255,255,255,0.25)', fontWeight: 700 },
  effectAfter: {},
  effectAfterLabel: { fontSize: 10, color: 'rgba(255,255,255,0.35)', marginBottom: 4 },
  effectAfterValue: { fontSize: 16, fontWeight: 800 },
  effectImprovement: { display: 'inline-block', padding: '4px 14px', borderRadius: 12, fontSize: 12, fontWeight: 700 },
  roiBanner: {
    display: 'flex', gap: 16, padding: '22px 24px', borderRadius: 18,
    background: 'linear-gradient(135deg, rgba(99,102,241,0.1) 0%, rgba(192,132,252,0.06) 100%)',
    border: '1px solid rgba(99,102,241,0.2)',
  },
  roiIcon: { fontSize: 32, flexShrink: 0 },
  roiTitle: { fontSize: 15, fontWeight: 700, color: '#a5b4fc', marginBottom: 8 },
  roiText: { fontSize: 13, color: 'rgba(255,255,255,0.65)', margin: 0, lineHeight: 1.8 },

  implTimeline: { display: 'flex', flexDirection: 'column', gap: 0, marginBottom: 24 },
  implItem: { display: 'flex', gap: 16 },
  implLeft: { display: 'flex', flexDirection: 'column', alignItems: 'center', width: 48, flexShrink: 0 },
  implNum: {
    width: 48, height: 48, borderRadius: 24, display: 'flex', alignItems: 'center',
    justifyContent: 'center', color: '#fff', fontSize: 16, fontWeight: 800,
  },
  implLine: { width: 2, flex: 1, minHeight: 16, background: 'rgba(255,255,255,0.1)' },
  implContent: { flex: 1, paddingBottom: 20 },
  implHeader: { display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 },
  implTitle: { fontSize: 16, fontWeight: 700 },
  implDuration: {
    padding: '2px 12px', borderRadius: 10, fontSize: 11, fontWeight: 600,
    background: 'rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.5)',
  },
  implDesc: { fontSize: 13, color: 'rgba(255,255,255,0.55)', margin: 0, lineHeight: 1.7 },
  ctaBanner: {
    padding: '24px 28px', borderRadius: 18, textAlign: 'center',
    background: 'linear-gradient(135deg, rgba(99,102,241,0.12) 0%, rgba(192,132,252,0.12) 100%)',
    border: '1px solid rgba(99,102,241,0.25)',
  },
  ctaText: { margin: 0, lineHeight: 1.8 },

  geoFlowWrap: {
    padding: '24px', borderRadius: 18, marginBottom: 24,
    background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)',
  },
  geoFlowTitle: {
    fontSize: 14, fontWeight: 700, color: 'rgba(255,255,255,0.6)', textAlign: 'center', marginBottom: 20,
  },
  geoFlow: { display: 'flex', alignItems: 'flex-start', justifyContent: 'center', gap: 6, flexWrap: 'wrap' },
  geoFlowStep: { textAlign: 'center', width: 120 },
  geoFlowIcon: {
    width: 48, height: 48, borderRadius: 24, margin: '0 auto 8px',
    background: 'rgba(99,102,241,0.12)', border: '1px solid rgba(99,102,241,0.25)',
    display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22,
  },
  geoFlowLabel: { fontSize: 12, fontWeight: 700, color: 'rgba(255,255,255,0.8)', marginBottom: 4 },
  geoFlowSub: { fontSize: 10, color: 'rgba(255,255,255,0.4)', lineHeight: 1.4, whiteSpace: 'pre-line' },
  geoFlowArrow: { color: 'rgba(255,255,255,0.2)', fontSize: 14, marginTop: 16 },
  geoValueGrid: { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 14 },
  geoValueCard: {
    padding: '20px', borderRadius: 16,
    background: 'rgba(255,255,255,0.04)', border: '1px solid',
  },
  geoValueNum: { fontSize: 28, fontWeight: 900, marginBottom: 8, opacity: 0.6 },
  geoValueTitle: { fontSize: 14, fontWeight: 700, marginBottom: 8 },
  geoValueDesc: { fontSize: 12, color: 'rgba(255,255,255,0.5)', lineHeight: 1.6, margin: 0 },

  footer: {
    textAlign: 'center', padding: '32px 0 0', marginTop: 40,
    borderTop: '1px solid rgba(255,255,255,0.08)',
  },
  footerText: { fontSize: 13, color: 'rgba(255,255,255,0.3)', margin: 0 },
  demoLink: {
    display: 'inline-block', marginTop: 10, fontSize: 11,
    color: 'rgba(255,255,255,0.12)', textDecoration: 'none',
    letterSpacing: 0.5, transition: 'color .2s',
  },
}
